import React, { useMemo, useEffect, useState } from "react";
import { PupsFundDogModel } from "../types/pupsFundDogModel";
import PupsFundDogCard from "./PupsFundDogCard";
import "./DogScroller.css";

interface DogScrollerProps {
  dogs: PupsFundDogModel[];
  baseAnimationDuration?: number; // Base duration in seconds
  mobileBaseAnimationDuration?: number; // Base duration for mobile in seconds
}

export default function DogScroller({
  dogs,
  baseAnimationDuration = 30,
  mobileBaseAnimationDuration = 40,
}: DogScrollerProps) {
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Create duplicated array to ensure at least 12 dogs total
  const createSufficientDogs = (originalDogs: PupsFundDogModel[]) => {
    if (originalDogs.length === 0) return [];

    // Calculate how many copies we need to reach at least 12 dogs
    const minimumDogsRequired = 11;
    const repetitions = Math.ceil(minimumDogsRequired / originalDogs.length);

    // Create an array with enough repetitions
    const expandedDogs = Array(repetitions)
      .fill([...originalDogs])
      .flat()
      .map((dog, index) => ({
        ...dog,
        uniqueKey: `${dog.id}-${index}`,
      }));

    return expandedDogs;
  };

  const displayDogs = createSufficientDogs(dogs);

  // Calculate animation duration based on number of dogs
  const animationDuration = useMemo(() => {
    if (displayDogs.length === 0)
      return isMobile ? mobileBaseAnimationDuration : baseAnimationDuration;

    // Scale the duration based on number of dogs
    // More dogs = longer duration to maintain consistent visual speed
    const baseDuration = isMobile
      ? mobileBaseAnimationDuration
      : baseAnimationDuration;
    return baseDuration * (displayDogs.length / 7);
  }, [
    displayDogs.length,
    baseAnimationDuration,
    mobileBaseAnimationDuration,
    isMobile,
  ]);

  // Apply the calculated duration as an inline style
  const slideStyle = {
    animationDuration: `${animationDuration}s`,
  };

  return (
    <div className="dogs-scroller">
      <div className="dogs-slide" style={slideStyle}>
        {displayDogs.map((dog) => (
          <div
            key={dog.uniqueKey || `${dog.id}-${Math.random()}`}
            className="dog-card-container"
          >
            <PupsFundDogCard dog={dog} />
          </div>
        ))}
      </div>
      <div className="dogs-slide" style={slideStyle}>
        {displayDogs.map((dog) => (
          <div
            key={
              `${dog.uniqueKey}-duplicate` ||
              `${dog.id}-duplicate-${Math.random()}`
            }
            className="dog-card-container"
          >
            <PupsFundDogCard dog={dog} />
          </div>
        ))}
      </div>
    </div>
  );
}
